import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { NodeDetailsDTO } from '../../common/services/env-configuration/node-details.dto';
import { map, share, Subscription, timer } from 'rxjs';
import { CohortDO } from '../../common/domain-model-dtos';
import { MediaMatcher } from '@angular/cdk/layout';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { EnvironmentService } from '../../common/services/env-configuration/environment.service';
import { Title } from '@angular/platform-browser';
import { LmTitleService } from '../../common/services/title.service';
import { SignInService } from '../../common/services/sign-in/sign-in.service';
import { SystemService } from '../../system/system.service';
import { AdminContextService } from '../../common/services/admin.context.service';
import moment from 'moment-timezone';
import { VERSION } from '../../version';
import { SETTINGS_SIDENAV_DEFINITION } from '../../shared/models/menu-group-def';
import { Environment } from '../manage/manage.component';
import { DatePipe } from '@angular/common';
import { PermissionService } from '../../core/services/permission.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false,
})
export class SettingsComponent implements OnInit {
  onResize(event) {
    this.isMediumScreen = event.target.innerWidth <= 1180;
    this.isSmallScreen = event.target.innerWidth <= 768;
  }

  private environments: Environment[] = [
    { env: 'DEV', name: 'DEVELOPMENT', color: '#0e75fd' },
    { env: 'STAGING', name: 'STAGING', color: '#0e75fd' },
    { env: 'PROD', name: 'PRODUCTION', color: '#7838f8' },
  ];

  public readonly mobileQuery: MediaQueryList;

  private readonly _mobileQueryListener: () => void;

  private menuDisplayBlackList = [/.*\/sso/, /^\/$/];

  public nodeDetailsDTO: NodeDetailsDTO;

  public shouldDisplayMenu;

  public version: string = VERSION;

  public sideMenuDisplayed = false;

  public timezones: string[] = [];

  public selectedTimezone = '';

  settingsToolbar = false;

  public isMediumScreen = false;

  public isSmallScreen = false;

  public menuGroupDef = SETTINGS_SIDENAV_DEFINITION;

  public time: string;
  timeSubscription: Subscription;

  public environment: Environment;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private router: Router,
    private route: ActivatedRoute,
    private envService: EnvironmentService,
    private titleService: Title,
    private lmTitleService: LmTitleService,
    private signInService: SignInService,
    private systemService: SystemService,
    private ngZone: NgZone,
    public adminContextService: AdminContextService,
    public datePipe: DatePipe,
    public permissionService: PermissionService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.permissionService.hasManagePermissions().subscribe();
  }

  ngOnInit(): void {
    this.isSmallScreen = window.innerWidth <= 768;
    this.isMediumScreen = window.innerWidth <= 1180;
    // this.timezones = moment.tz.names();
    this.timezones = [
      'Europe/London',
      'Europe/Madrid',
      'UTC',
      'Pacific/Wallis',
      'US/Pacific',
      'US/Eastern',
      'US/Central',
    ];
    this.selectedTimezone = 'UTC';

    this.systemService.systemTimeZoneSubject.next(this.selectedTimezone);

    this.determineMenuDisplay();
    this.envService.getNodeInfoObs().subscribe((details) => {
      this.nodeDetailsDTO = details;
      this.titleService.setTitle(`${details.instanceType} | Admin`);
      this.lmTitleService.setPrefix(details?.instanceType);
      this.environment = this.getEnv(details.operationalMode);
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.determineMenuDisplay();
      }
    });

    this.adminContextService.getCohortWatcher().subscribe((cohort: CohortDO) => {
      if (cohort) {
        this.lmTitleService.setPrefix(cohort.code);
      }
      this.changeDetectorRef.detectChanges();
    });

    this.timeSubscription = timer(0, 1000)
      .pipe(
        map(() => new Date()),
        share()
      )
      .subscribe((time) => {
        this.time = this.datePipe.transform(time, 'hh:mm:ss a', 'UTC');
      });
  }

  public selectionChange(timezone: string): void {
    this.systemService.systemTimeZoneSubject.next(timezone);
    this.selectedTimezone = timezone;
    moment.tz.setDefault(timezone);
  }

  public logout() {
    this.signInService
      .getCurrentUser()
      .signOut()
      .then(() => {
        this.ngZone.run(() => this.router.navigateByUrl('/sso'));
        this.ngOnDestroy();
      });
  }

  private determineMenuDisplay() {
    this.shouldDisplayMenu = this.menuDisplayBlackList.filter((url) => this.router.url.match(url)).length === 0;
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    if (this.timeSubscription) {
      this.timeSubscription.unsubscribe();
    }
  }

  toggleSideMenu(snav) {
    snav.toggle();
    this.sideMenuDisplayed = !this.sideMenuDisplayed;
  }

  getEnv(env: string): Environment {
    return this.environments.find((value: Environment) => value.env == env);
  }

  navigateHome() {
    this.router.navigate(['./'], { relativeTo: this.route });
  }
}
